export type ProductSlug = 'bubbles' | 'group-upp';
export type ProductPageKind = 'privacy' | 'support' | 'terms';

export type ProductPage = {
  product: 'Bubbles' | 'Group Upp';
  title: string;
  eyebrow: string;
  version: string;
  updated: string;
  summary: string;
  contactEmail: string;
  sections: Array<{ title: string; body: string[] }>;
};

export const productPages: Record<ProductSlug, Record<ProductPageKind, ProductPage>> = {
  bubbles: {
    privacy: {
      product: 'Bubbles',
      title: 'Privacy Policy',
      eyebrow: 'Your data',
      version: '2026-08-09',
      updated: '9 August 2026',
      contactEmail: 'contact@echosol.co.uk',
      summary: 'This policy describes the personal information Bubbles handles, why it is used, who can see it, and the choices available to you.',
      sections: [
        {
          title: '1. Data controller',
          body: [
            'Bubbles is owned and operated by Echosol Solutions Ltd, a Scottish private company limited by shares registered under company number SC896357. Echosol Solutions Ltd is the controller of personal information processed through Bubbles.',
            'For privacy questions, rights requests, support, legal questions or account requests, email contact@echosol.co.uk.',
          ],
        },
        {
          title: '2. Information we process',
          body: [
            'Account and profile data includes your email address, display name, profile details, profile photo, privacy settings and authentication identifiers.',
            'App content may include ratings, tasting notes, drink selections, serving details, venues, cities, collection entries, tasting photos, drink request photos, follows, reports, complaints and catalog corrections.',
            'Bubbles also processes saved items, blocks, notification records, moderation status, timestamps and account status where needed to run the service.',
          ],
        },
        {
          title: '3. Age check',
          body: [
            'Because Bubbles contains extensive information about alcoholic drinks, users must confirm their date of birth before using the app. Bubbles is only available to people aged 21 or older, even if local law allows alcohol consumption at a younger age.',
            'The date of birth used for the age gate is stored on your device and is not uploaded to Bubbles unless a future version clearly says otherwise and asks for any required consent.',
          ],
        },
        {
          title: '4. Location information',
          body: [
            'A tasting can include a city and country that you enter. If you choose “Use current city”, the app briefly uses device location to resolve your city.',
            'Bubbles stores a city-level location for the tasting, not your precise GPS position, and does not track you in the background. You can deny location permission and enter a city manually.',
          ],
        },
        {
          title: '5. Social visibility',
          body: [
            'Public tastings and public profile information can be seen by other users. Private tastings remain visible only to you. A private profile requires approval for new followers.',
            'Review the visibility choice each time you log a tasting. Other users may save or share content outside Bubbles even after you remove it.',
          ],
        },
        {
          title: '6. Technical information',
          body: [
            'Firebase and app-security services may process authentication identifiers, device and app-integrity signals, timestamps, diagnostic information and network information needed to sign you in, secure the service and investigate failures or abuse.',
            'Bubbles currently declares no advertising tracking and the Firebase configuration reviewed for the app has analytics disabled. This must be kept aligned with the final production build and provider settings.',
          ],
        },
        {
          title: '7. How information is used',
          body: [
            'We use information to operate your account, sync your collection, build your taste profile and My Atlas, provide social features, moderate reports and corrections, secure Bubbles, troubleshoot problems and improve the service.',
            'Bubbles does not sell alcohol and does not verify whether a drink is suitable for you. Product information is informational, not medical, dietary or legal advice.',
          ],
        },
        {
          title: '8. Lawful bases',
          body: [
            'Contract: account and service data is processed to provide the app features you request, including sign-in, profiles, collections, tasting logs, saved drinks, follows, moderation tools, support and account deletion.',
            'Consent: optional device permissions, such as device location, are used with your permission. You can withdraw device permissions in system settings.',
            'Legitimate interests: security, fraud prevention, moderation, service integrity, troubleshooting and service improvement may rely on legitimate interests where permitted. We balance those interests against your rights and expectations.',
            'Legal obligation and legal claims: we may process or retain information where necessary to comply with law, respond to valid authority requests, protect legal rights, or establish, exercise or defend claims.',
          ],
        },
        {
          title: '9. Service providers and disclosures',
          body: [
            'Bubbles uses Google Firebase for authentication, database, storage, cloud functions and app protection. Apple and Google may also process information independently when you download the app or use their platform services.',
            'We may disclose information to professional advisers, insurers, courts, regulators, law-enforcement bodies, or a buyer or reorganised business where reasonably necessary and lawful.',
            'We do not sell personal information and do not use advertising tracking.',
          ],
        },
        {
          title: '10. Retention and deletion',
          body: [
            'We keep account information while your account is active and as needed for security, legal obligations, provider backups and dispute handling.',
            'You can request deletion in the app through Settings > Delete account. The deletion flow removes your account, profile, tastings, follows, blocks, saved items and account-linked uploaded media server-side. If the in-app flow fails, email contact@echosol.co.uk from the address on your account.',
          ],
        },
        {
          title: '11. Your choices and rights',
          body: [
            'You can choose tasting visibility, make your profile private, disable in-app notifications, omit tasting locations, deny location permission, correct profile details and delete your account in the app.',
            'Depending on the circumstances, UK data-protection law gives you rights to be informed; access personal data; correct inaccurate data; request erasure or restriction; receive portable data; object to legitimate-interest processing; and withdraw consent.',
            'We may need to verify your identity before acting on a request. You may complain to the UK Information Commissioner at ico.org.uk or by calling 0303 123 1113.',
          ],
        },
        {
          title: '12. Security, transfers and updates',
          body: [
            'We use reasonable technical and organisational safeguards, but no online service is completely risk-free. Tell us promptly if you believe your account or the service has been compromised.',
            'Service providers may process information outside the United Kingdom. Where required, we use legally recognised transfer safeguards.',
            'Updates to this policy will be identified by a new effective date.',
          ],
        },
      ],
    },
    support: {
      product: 'Bubbles',
      title: 'Support',
      eyebrow: 'Help and requests',
      version: '2026-08-09',
      updated: '9 August 2026',
      contactEmail: 'contact@echosol.co.uk',
      summary: 'Use this page for Bubbles support, account deletion help, complaints, safety reports and privacy requests.',
      sections: [
        {
          title: '1. Contact support',
          body: [
            'Email contact@echosol.co.uk for account, technical, catalogue, privacy, legal or safety support.',
            'Include the email address on your account, your device model, the app version if known, and a clear description of the problem. Do not include passwords or unnecessary sensitive information.',
          ],
        },
        {
          title: '2. Account deletion',
          body: [
            'The preferred deletion route is Settings > Delete account in the Bubbles app. You will be asked to type DELETE and re-enter your password before the deletion is submitted.',
            'Deletion removes your account, profile, tastings, follows, blocks, saved items and uploaded account-linked media from the server-side systems. If the in-app flow fails, email contact@echosol.co.uk from the address on your account.',
            'Limited records may be retained where necessary for security, legal obligations, provider backups, moderation history or dispute handling.',
          ],
        },
        {
          title: '3. Complaints and reports',
          body: [
            'In the app, use Complaints for content complaints, user complaints, safety issues, account issues, technical issues or other requests that need direct team review.',
            'You can report public tastings and other visible content for spam or inappropriate content. The Bubbles team may review the report, hide content, correct catalogue information, restrict accounts or dismiss the report where appropriate.',
          ],
        },
        {
          title: '4. Community standards',
          body: [
            'Bubbles asks users to be accurate, respectful, lawful and privacy-conscious. Do not encourage under-age drinking, unsafe drinking, drink-driving, harassment, unlawful activity, impersonation, false corrections, scraping, spam or abusive reporting.',
            'Content or accounts may be restricted or removed to protect users and the service.',
          ],
        },
        {
          title: '5. Drink information and safety',
          body: [
            'Catalogue information, alcohol strength, ingredients, allergens, recipes, origins and tasting notes may be incomplete or change over time. Check the product label and ask the producer or venue when accuracy is important.',
            'A rating or tasting note is an opinion, not a recommendation to consume alcohol. Drink responsibly and follow local laws.',
          ],
        },
        {
          title: '6. Privacy rights',
          body: [
            'For access, correction, deletion, portability, restriction, objection or consent-withdrawal requests, email contact@echosol.co.uk. We may need to verify your identity before responding.',
          ],
        },
      ],
    },
    terms: {
      product: 'Bubbles',
      title: 'Terms & Conditions',
      eyebrow: 'Using Bubbles',
      version: '2026-08-09',
      updated: '9 August 2026',
      contactEmail: 'contact@echosol.co.uk',
      summary: 'These terms explain the rules for creating an account and using Bubbles. Before using the app, you must pass the age check and agree to these terms.',
      sections: [
        {
          title: '1. Who may use Bubbles',
          body: [
            'You must be able to enter a binding agreement where you live. Because Bubbles contains extensive information about alcoholic drinks, users must confirm their date of birth before using the app.',
            'Bubbles is only available to people aged 21 or older, even if local law allows alcohol consumption at a younger age.',
          ],
        },
        {
          title: '2. What Bubbles provides',
          body: [
            'Bubbles helps you discover drinks, record tastings, keep a collection, learn about styles and origins, and share selected activity with other users.',
            'Bubbles does not sell alcohol and does not verify whether a drink is suitable for you.',
          ],
        },
        {
          title: '3. Drink information',
          body: [
            'Catalogue information, alcohol strength, ingredients, allergens, recipes, origins and tasting notes may be incomplete or change over time.',
            'Check the product label and ask the producer or venue when accuracy is important. Content is informational, not medical or dietary advice.',
          ],
        },
        {
          title: '4. Responsible use',
          body: [
            'Drink responsibly and follow local laws. Never use Bubbles to encourage dangerous consumption, underage drinking, drink-driving, harassment or unlawful activity.',
            'A rating is an opinion, not a recommendation to consume alcohol.',
          ],
        },
        {
          title: '5. Your account',
          body: [
            'Keep your credentials secure and provide accurate account information. You are responsible for activity performed through your account unless it results from our failure to use reasonable care.',
            'Email contact@echosol.co.uk if you believe your account has been compromised.',
          ],
        },
        {
          title: '6. Your content',
          body: [
            'You retain ownership of tasting notes, ratings, profile content and photos you create.',
            'You give Bubbles permission to store, process and display that content as needed to operate the service and according to the visibility you choose. Do not upload content you do not have the right to use.',
          ],
        },
        {
          title: '7. Community conduct',
          body: [
            'Do not impersonate others, scrape the catalogue, interfere with the service, submit misleading corrections, or post abusive, discriminatory, infringing or illegal material.',
            'Content or accounts may be restricted or removed to protect users and the service.',
          ],
        },
        {
          title: '8. Availability and changes',
          body: [
            'Features and catalogue data may be changed, suspended or discontinued. We may update these terms when the service changes or when required by law.',
            'Material changes will use a new effective date and may require renewed acceptance.',
          ],
        },
        {
          title: '9. Liability and consumer rights',
          body: [
            'To the extent permitted by law, Bubbles is provided without a guarantee that it will always be available or error-free.',
            'Nothing in these terms excludes rights or liabilities that cannot legally be excluded, including liability for death or personal injury caused by negligence, fraud, fraudulent misrepresentation, or your statutory consumer rights.',
          ],
        },
        {
          title: '10. Operator and contact',
          body: [
            'Bubbles is owned and operated by Echosol Solutions Ltd. For support, legal questions or account requests, email contact@echosol.co.uk.',
          ],
        },
      ],
    },
  },
  'group-upp': {
    privacy: {
      product: 'Group Upp',
      title: 'Privacy Policy',
      eyebrow: 'Your data',
      version: '2026-09-12',
      updated: '12 September 2026',
      contactEmail: 'kasper.skrumsager@echosol.co.uk',
      summary: 'This policy explains how Group Upp collects, uses, shares, retains and protects personal data in the mobile app and related support, invitation, sharing and account services.',
      sections: [
        {
          title: '1. Who we are',
          body: [
            'Group Upp is operated by Echosol Solutions Ltd, a Scottish private company limited by shares registered under company number SC896357. Echosol Solutions Ltd is the controller of personal data processed for Group Upp.',
            'Primary privacy and support contact: kasper.skrumsager@echosol.co.uk. Alternative privacy contact: privacy@echosol.co.uk.',
          ],
        },
        {
          title: '2. Data we collect',
          body: [
            'Account and profile data includes email address, username, display name, profile image, date of birth, Firebase user ID, account status, and the date and version of the legal terms you accepted.',
            'Social and group data includes groups you create or join, group roles and invitations, friends and friend requests, blocked users, referral code and referral activity, and group/profile customisation.',
            'Content and activity data includes images, quotes, jokes, captions, puzzle completion posts, votes, scores, rankings, leaderboard results, badges, streaks, puzzle answers and progress, report details, and related timestamps.',
            'Device, communications and diagnostic data may include notification preferences, push token, installation identifier, platform, device name/model, app environment, support or feedback messages, crash reports, app errors, technical logs and IP/network information.',
            'AI feature data is processed if you use an AI referral reward, including the selected category or prompt, generated text or image, safety-related metadata, and the user ID needed to check eligibility and deliver the result.',
            'Theme settings, notification installation information and saved puzzle progress may be stored locally on your device.',
          ],
        },
        {
          title: '3. Purposes and lawful bases',
          body: [
            'Contract: to create and secure your account; provide groups, posts, voting, puzzles, leaderboards, referrals and rewards; save preferences; deliver requested notifications; and provide account deletion and support.',
            'Legitimate interests: to keep the service reliable and secure, diagnose faults, understand feature performance, prevent cheating, spam and abuse, moderate reports, enforce the Terms, and protect users and the business. We balance these interests against your rights and may provide an objection or opt-out where required.',
            'Consent: where the law requires it, including device permissions and optional push notifications. You can withdraw a device permission in system settings and change notification choices in the app. Withdrawal does not affect earlier lawful processing.',
            'Legal obligation and legal claims: to comply with law, respond to valid authority requests, protect legal rights, and establish, exercise or defend claims.',
            'We do not use Group Upp data for decisions that produce legal or similarly significant effects. Puzzle scores, weekly points, rankings and badges are calculated automatically from app activity but are recreational only.',
          ],
        },
        {
          title: '4. Who can see your information',
          body: [
            'Your username, display name and profile image may be visible to other signed-in users. Group membership, posts, submissions, votes, puzzle results, rankings and badges may be visible to members of the relevant groups or to users using the connected feature.',
            'A post submitted to several groups may identify those groups to you and may be delivered separately within each relevant group. Group owners or administrators may receive reports about content in their group. App administrators may receive reports sent to Group Upp.',
            'Do not post personal or confidential information that you do not want the relevant audience to see. Other users may save or share content outside Group Upp even after you remove it.',
          ],
        },
        {
          title: '5. Service providers and disclosures',
          body: [
            'Google Firebase and Google Cloud provide authentication, database, file storage, server functions and related infrastructure. Expo provides app build and push-notification delivery services.',
            'Sentry provides error monitoring and diagnostic reporting. OpenAI processes prompts and generates text or images only when an AI-powered reward is requested.',
            'Apple and Google also process information independently when you download the app or use their platform services. Their privacy notices apply to that processing.',
            'We may disclose information to professional advisers, insurers, courts, regulators, law-enforcement bodies, or a buyer or reorganised business where reasonably necessary and lawful. We do not sell personal data and currently do not use third-party behavioural advertising.',
          ],
        },
        {
          title: '6. International transfers',
          body: [
            'Some providers or their support teams may process data outside the United Kingdom. Where UK adequacy regulations do not apply, we use an appropriate UK transfer mechanism, such as the UK International Data Transfer Agreement or UK Addendum, together with any required transfer-risk assessment and supplementary safeguards.',
          ],
        },
        {
          title: '7. Retention and deletion',
          body: [
            'We keep account, social, content, competition and preference data while your account is active and for as long as needed to provide the relevant feature. Local puzzle progress remains until you clear it, uninstall the app, or the operating system removes it.',
            'When you schedule account deletion, there is a seven-day grace period in which you may cancel. After that, the app is designed to delete your account, authentication record, public profile, group memberships, friendships, posts and submissions, puzzle completions, votes, notification tokens and other data stored under your user account.',
            'Limited moderation, fraud, security, legal-claim, transaction/audit and provider backup records may be retained after account deletion where necessary. We retain those records only for the applicable legal, security or operational period and then delete or anonymise them. Content copied or shared by another user is not controlled by your account deletion.',
          ],
        },
        {
          title: '8. Your rights',
          body: [
            'Depending on the circumstances, UK data-protection law gives you rights to be informed; obtain access and a copy; correct inaccurate data; request erasure or restriction; receive portable data; object to legitimate-interest processing; and withdraw consent.',
            'Email kasper.skrumsager@echosol.co.uk or privacy@echosol.co.uk to exercise a right. We may need to verify your identity. We normally respond within one month, subject to lawful extensions. You may also delete your account in Settings.',
            'You may complain to the UK Information Commissioner at ico.org.uk or by calling 0303 123 1113. Please contact us first if you are comfortable doing so, so we can try to resolve the issue.',
          ],
        },
        {
          title: '9. Children',
          body: [
            'Group Upp is not intended for children under 16, and a person under 16 must not create an account. If you are under 18, you should review these documents with a parent or guardian because the Online Safety Act treats users under 18 as children for safety purposes.',
            'We collect date of birth at sign-up to verify age eligibility. If we reasonably believe an under-16 account exists, we may restrict it and request age information, or delete it. A parent or guardian can contact us about a child’s data using the privacy email above.',
          ],
        },
        {
          title: '10. Security and changes',
          body: [
            'We use access controls, authenticated services and other organisational and technical measures intended to protect personal data. No online service can guarantee absolute security. Tell us promptly if you believe an account or the service has been compromised.',
            'We may update this policy as the app, providers or law change. Material changes will be brought to users’ attention in an appropriate way, and we will seek fresh consent where required.',
          ],
        },
      ],
    },
    support: {
      product: 'Group Upp',
      title: 'Support',
      eyebrow: 'Help and safety',
      version: '2026-09-12',
      updated: '12 September 2026',
      contactEmail: 'kasper.skrumsager@echosol.co.uk',
      summary: 'Use this page for Group Upp account help, privacy requests, safety concerns, moderation appeals, copyright complaints and account deletion support.',
      sections: [
        {
          title: '1. Contact support',
          body: [
            'Email kasper.skrumsager@echosol.co.uk for product support, legal questions, account help, safety concerns, complaints or moderation appeals. You may also use privacy@echosol.co.uk for privacy requests.',
            'Include your username, user ID if available, device, app version if known, and enough detail for us to investigate. Do not include passwords or unnecessary sensitive information.',
          ],
        },
        {
          title: '2. Account deletion',
          body: [
            'You can schedule account deletion in Settings. The request starts a seven-day grace period, and you can cancel during that period.',
            'After the grace period, the deletion process is designed to remove the account, authentication record, public profile, group memberships, friendships, posts and submissions, puzzle completions, votes, notification tokens and other information stored under your user account.',
            'Limited moderation, fraud, security, legal-claim, transaction/audit and provider backup records may be retained where necessary.',
          ],
        },
        {
          title: '3. Reports, blocking and safety',
          body: [
            'You can report feed content to a group owner or administrator, to Group Upp, or to both. You can also hide another user’s feed posts.',
            'For urgent danger, contact emergency services. Group Upp reporting and support channels are not emergency services.',
            'We may review reports and content; reduce visibility; remove content; correct results; limit features; or warn, suspend or terminate accounts where reasonably necessary for safety, legal compliance, the Terms or service integrity.',
          ],
        },
        {
          title: '4. Moderation appeals',
          body: [
            'If we take material action against your content or account, we will provide a reason where appropriate and lawful.',
            'To request review or appeal, email kasper.skrumsager@echosol.co.uk with your username, the decision, the content involved, and why you believe the decision should change.',
          ],
        },
        {
          title: '5. Copyright complaints',
          body: [
            'To report infringement, email kasper.skrumsager@echosol.co.uk with your name and contact details, identification of the protected work, the exact Group Upp content or account, why the use is unauthorised, and a statement that your report is accurate and made by the rightsholder or an authorised representative.',
            'We may request evidence, remove or restrict content, notify the uploader, and take action against repeat infringement. Knowingly false reports may create legal liability.',
          ],
        },
        {
          title: '6. Privacy rights',
          body: [
            'For access, correction, deletion, portability, restriction, objection or consent-withdrawal requests, email kasper.skrumsager@echosol.co.uk or privacy@echosol.co.uk. We may need to verify your identity before responding.',
          ],
        },
      ],
    },
    terms: {
      product: 'Group Upp',
      title: 'Terms of Service',
      eyebrow: 'Using Group Upp',
      version: '2026-09-12',
      updated: '12 September 2026',
      contactEmail: 'kasper.skrumsager@echosol.co.uk',
      summary: 'These terms explain the rules for using Group Upp, posting content, taking part in groups and puzzles, and using referrals or rewards.',
      sections: [
        {
          title: '1. Agreement and operator',
          body: [
            'These Terms form an agreement between you and Echosol Solutions Ltd, a Scottish private company limited by shares registered under company number SC896357 and the United Kingdom operator of Group Upp.',
            'They apply to the Group Upp app and related services. By creating an account, you agree to these Terms and acknowledge the Privacy Policy. If you do not agree, do not create or use an account.',
          ],
        },
        {
          title: '2. Eligibility and accounts',
          body: [
            'You must be at least 16 to create an account. You will be asked to provide your date of birth so we can verify eligibility. If you are under 18, you confirm that a parent or guardian has reviewed and permitted your use.',
            'Provide accurate information, use one account per person unless we agree otherwise, keep credentials secure, and tell us promptly about unauthorised access.',
            'Usernames and group names must not impersonate others, violate rights, mislead users, or contain prohibited content. We may require a name to be changed.',
          ],
        },
        {
          title: '3. The service',
          body: [
            'Group Upp lets users create and join groups, connect with friends, submit images, quotes, jokes and puzzle results, vote, receive notifications, use referrals and rewards, and take part in practice puzzles, daily races, rankings and weekly leaderboards.',
            'Puzzle and competition results are for entertainment. Points, badges, votes, referral progress and rewards have no cash value, are not transferable, and are not gambling or a promise of a prize.',
            'Some puzzles or races are available only during a stated period. Device time, connectivity and service outages may affect availability.',
          ],
        },
        {
          title: '4. Your content and licence',
          body: [
            'You retain ownership of content you create. You are responsible for it and must have all rights and permissions needed to upload, display and share it, including permission from identifiable people where required.',
            'When you submit content, you grant Echosol Solutions Ltd a non-exclusive, worldwide, royalty-free licence to host, store, reproduce, adapt for technical display, communicate and show that content as needed to operate, secure, moderate and promote the relevant in-app feature. We may sublicense this limited licence to service providers.',
            'The licence ends when the content is deleted except for lawful backups, moderation/evidence records, content already shared by others, and rights needed to resolve a dispute. Giving credit is not always permission; check sources and licences or use original content.',
          ],
        },
        {
          title: '5. Acceptable use',
          body: [
            'Do not post or promote unlawful content; child sexual abuse material; sexual exploitation; terrorism; credible threats; harassment; hateful or discriminatory abuse; non-consensual intimate content; encouragement of suicide or serious self-harm; fraud; defamation; privacy violations; intellectual-property infringement; dangerous illegal activity; or sexual content involving minors.',
            'Do not impersonate, stalk or exploit another person; expose private information; spam; distribute malware; scrape or reverse engineer the service except where law permits; bypass safeguards; interfere with systems; or use automated accounts without permission.',
            'Do not manipulate votes, scores, leaderboards, referrals or rewards, coordinate dishonest voting, exploit bugs, create fake accounts, or offer value in exchange for engagement. Group owners and administrators must not use their powers for harassment, discrimination, retaliation or unlawful purposes.',
          ],
        },
        {
          title: '6. Reporting, blocking and moderation',
          body: [
            'You can report feed content to a group owner or administrator, to Group Upp, or to both, and can hide another user’s feed posts. For urgent danger, contact emergency services; the reporting tool is not an emergency channel.',
            'We may review reports and content; reduce visibility; remove content; correct results; limit features; or warn, suspend or terminate accounts when reasonably necessary for safety, legal compliance, these Terms or service integrity. Group administrators may also remove content within their group.',
            'We aim to assess reports proportionately and without unlawful discrimination. You may request review or appeal by emailing kasper.skrumsager@echosol.co.uk with your username, the decision and why you believe it should change.',
          ],
        },
        {
          title: '7. Copyright complaints',
          body: [
            'To report infringement, email kasper.skrumsager@echosol.co.uk with your name and contact details, identification of the protected work, the exact Group Upp content or account complained about, why the use is unauthorised, and a statement that the report is accurate and made by the rightsholder or an authorised representative.',
            'We may request evidence, remove or restrict content, notify the uploader, and act against repeat infringement. An uploader may reply with evidence of ownership, licence or another lawful basis.',
          ],
        },
        {
          title: '8. AI-powered rewards',
          body: [
            'Optional referral rewards may use OpenAI to generate text, an image or a photo prompt. Output is probabilistic and may be inaccurate, inappropriate, non-unique or similar to existing material. It is not professional advice, and we do not guarantee that it can be owned, registered or used without infringing rights.',
            'You must review generated output before posting or using it and must not request prohibited, unlawful, deceptive or rights-infringing material. Report unsafe generated output through the available report or support channel.',
          ],
        },
        {
          title: '9. Our rights and third-party services',
          body: [
            'The app, brand, interface, code and operator-created content are owned by or licensed to Echosol Solutions Ltd. These Terms give you a personal, limited, revocable, non-transferable right to use the service for its intended non-commercial purpose.',
            'The service relies on third parties such as app stores, Firebase, Expo, Sentry and OpenAI. Their own terms may apply. Apple and Google are not responsible for Group Upp support, content or claims except to the extent their platform terms require.',
          ],
        },
        {
          title: '10. Changes, availability and ending use',
          body: [
            'We may improve, replace, suspend or discontinue features. We will use reasonable care and give appropriate notice of a material adverse change where practicable. We do not guarantee that every feature, puzzle, post or group will always be available.',
            'You may stop using the service and schedule account deletion in Settings. We may restrict or end access for a serious or repeated breach, legal requirement, safety risk, fraud, or material threat to the service. Where appropriate, we will explain the action and provide a review route.',
          ],
        },
        {
          title: '11. Responsibility and consumer rights',
          body: [
            'We provide the service with reasonable care and skill. Nothing in these Terms excludes statutory rights or liability that cannot lawfully be excluded, including liability for death or personal injury caused by negligence, fraud, or fraudulent misrepresentation.',
            'We are not responsible for loss that was not reasonably foreseeable when you agreed to these Terms, for business loss arising from personal use, or for events outside our reasonable control. We are not responsible for another user’s content or conduct, but this does not remove any duty the law places on us.',
          ],
        },
        {
          title: '12. Complaints, law and changes',
          body: [
            'Send complaints to kasper.skrumsager@echosol.co.uk or privacy@echosol.co.uk with enough detail for us to investigate. Privacy complaints and rights requests are handled under the Privacy Policy.',
            'These Terms are governed by the law of England and Wales. If you are a UK consumer, you may bring proceedings in the courts of the UK country where you live. If mandatory consumer law where you live gives you additional rights, these Terms do not remove them.',
            'We may update these Terms for legal, safety, technical or service reasons. We will identify the new version and give appropriate notice of material changes. If fresh agreement is legally required, we will ask for it before continued account use.',
          ],
        },
      ],
    },
  },
};
